import {ConfigsAPI} from 'app/client/ui/ConfigsAPI';
import {DiscordAllowlist, DiscordAllowlistEntry} from 'app/common/Config';
import {Disposable, Observable} from 'grainjs';

export interface DiscordAllowlistModel {
  readonly entries: Observable<DiscordAllowlistEntry[] | null>;
  fetch(): Promise<void>;
  add(entry: DiscordAllowlistEntry): Promise<void>;
  remove(discordUserId: string): Promise<void>;
}

export interface DiscordAllowlistModelOptions {
  configsAPI: ConfigsAPI;
}

export class DiscordAllowlistModelImpl extends Disposable implements DiscordAllowlistModel {
  public readonly entries: Observable<DiscordAllowlistEntry[] | null> =
    Observable.create(this, null);
  private readonly _configsAPI = this._options.configsAPI;

  constructor(private _options: DiscordAllowlistModelOptions) {
    super();
  }

  public async fetch(): Promise<void> {
    this.entries.set(null);
    try {
      const {value} = await this._configsAPI.getConfig('discord_allowlist');
      if (this.isDisposed()) { return; }
      this.entries.set((value as DiscordAllowlist).users ?? []);
    } catch (e) {
      if (e.status === 404) {
        this.entries.set([]);
      } else {
        throw e;
      }
    }
  }

  public async add(entry: DiscordAllowlistEntry): Promise<void> {
    const current = this.entries.get() ?? [];
    if (current.some(e => e.discordUserId === entry.discordUserId)) {
      return; // already present, no-op
    }
    await this._save([...current, entry]);
  }

  public async remove(discordUserId: string): Promise<void> {
    const current = this.entries.get() ?? [];
    await this._save(current.filter(e => e.discordUserId !== discordUserId));
  }

  private async _save(users: DiscordAllowlistEntry[]): Promise<void> {
    const allowlist: DiscordAllowlist = {users};
    await this._configsAPI.updateConfig('discord_allowlist', allowlist);
    if (this.isDisposed()) { return; }
    this.entries.set(users);
  }
}
